/**
 * 训练模型重命名弹窗
 * v10.2 新增
 */

import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Pencil, Loader2 } from 'lucide-react'
import type { VoiceTrainingModel } from '@/modules/identity/types'

interface VoiceModelRenameDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  model: VoiceTrainingModel | null
  onConfirm: (newName: string) => Promise<void>
  isRenaming?: boolean
}

export function VoiceModelRenameDialog({
  open,
  onOpenChange,
  model,
  onConfirm,
  isRenaming = false,
}: VoiceModelRenameDialogProps) {
  const [newName, setNewName] = useState('')

  // 弹窗打开时设置初始值
  useEffect(() => {
    if (open && model) {
      setNewName(model.name)
    }
  }, [open, model])

  const canConfirm = newName.trim().length > 0 && newName !== model?.name

  const handleConfirm = async () => {
    if (!canConfirm) return
    await onConfirm(newName.trim())
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && canConfirm && !isRenaming) {
      handleConfirm()
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-5 w-5 text-violet-500" />
            重命名训练模型
          </DialogTitle>
          <DialogDescription>
            为模型设置一个易于识别的名称
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="model-name">当前名称</Label>
            <p className="text-sm text-slate-500 bg-slate-50 p-2 rounded">
              {model?.name}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="new-name">新名称</Label>
            <Input
              id="new-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="输入新名称..."
              maxLength={50}
              autoFocus
            />
            <p className="text-xs text-slate-400">
              建议使用日期或描述性名称，如「清晰版」「2025-01-27」
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isRenaming}
          >
            取消
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!canConfirm || isRenaming}
          >
            {isRenaming ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                保存中...
              </>
            ) : (
              '确认'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
